import { useState } from 'react';
import { Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import AppointmentModal from '../components/AppointmentModal';

const MOUTH_GUARD_BENEFITS = [
  'Prevent enamel wear',
  'Reduce jaw pain',
  'Protect restorations',
  'Improve long-term oral health',
  'Custom fit for maximum comfort',
  'Superior protection vs store-bought',
];

const MOUTH_GUARD_USES = [
  { title: 'Teeth Grinding (Bruxism)', description: 'Protect teeth from nighttime grinding damage' },
  { title: 'TMJ Protection', description: 'Reduce jaw joint stress and discomfort' },
  { title: 'Sports Protection', description: 'Shield teeth during athletic activities' },
  { title: 'Jaw Stabilization', description: 'Maintain proper jaw alignment' },
];

export default function MouthGuardsPage() {
  const [showAppointment, setShowAppointment] = useState(false);

  return (
    <div className="pt-24 lg:pt-32" data-testid="mouth-guards-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Custom Mouth Guards
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Professionally fitted mouth guards designed to protect your teeth, reduce jaw pain, and provide superior comfort.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white" data-testid="mouth-guards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Professional-Grade Protection
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Unlike over-the-counter options, our custom mouth guards are professionally fitted to your exact dental anatomy. This ensures maximum comfort, superior protection, and a secure fit that won't slip or cause discomfort.
              </p>
              
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Benefits of Custom Mouth Guards:</h3>
                <ul className="space-y-3">
                  {MOUTH_GUARD_BENEFITS.map((benefit, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      <span className="text-slate-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => setShowAppointment(true)}
                className="mt-10 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6"
              >
                Get a Custom Mouth Guard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-orange-100 rounded-3xl transform -rotate-3" />
              <div className="relative rounded-3xl overflow-hidden shadow-lg aspect-square bg-slate-200">
                <img
                  src="https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/2hjm4i49_custom-mouth-guards-2-1.webp"
                  alt="Custom mouth guard"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              When You Need a Custom Mouth Guard
            </h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              Our custom mouth guards are designed for various needs and activities.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MOUTH_GUARD_USES.map((use, idx) => (
              <div key={idx} className="bg-slate-800 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-orange-600/20 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{use.title}</h3>
                <p className="text-slate-400 text-sm">{use.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Custom Fitting Process
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Consultation</h3>
              <p className="text-slate-600">We assess your needs and take precise impressions of your teeth.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Fabrication</h3>
              <p className="text-slate-600">Your custom mouth guard is crafted in our lab for a perfect fit.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Fitting</h3>
              <p className="text-slate-600">We ensure your guard fits perfectly and make any needed adjustments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="mg-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready for Better Protection?
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Schedule a consultation to discuss your needs and get fitted for a custom mouth guard.
          </p>
          <Button
            onClick={() => setShowAppointment(true)}
            className="mt-10 bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
            data-testid="mg-schedule-btn"
          >
            Schedule Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
